import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getApp,
  getApps,
  getMe,
  getPlatformVersion,
  getWorkspace,
  getWorkspaceEntryBranding,
  getWorkspaceSubdomain,
  login,
  logout,
} from "./client";

export const APP_CATALOG_REFRESH_INTERVAL_MS = 15_000;
export const DEPLOYMENT_REFRESH_INTERVAL_MS = 30_000;

export const platformVersionQuery = queryOptions({
  queryKey: ["platform-deployment-version"],
  queryFn: getPlatformVersion,
  retry: false,
  staleTime: DEPLOYMENT_REFRESH_INTERVAL_MS,
  refetchInterval: DEPLOYMENT_REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: false,
  refetchOnReconnect: "always",
  refetchOnWindowFocus: "always",
});

export const workspaceEntryBrandingQuery = queryOptions({
  queryKey: ["workspace-entry-branding"],
  queryFn: getWorkspaceEntryBranding,
  retry: false,
  staleTime: 5 * 60_000,
});

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: getMe,
  retry: false,
  staleTime: 30_000,
});

export function appsQuery(organizationId: string) {
  return queryOptions({
    queryKey: ["organizations", organizationId, "apps"],
    queryFn: () => getApps(organizationId),
    enabled: organizationId.length > 0,
    staleTime: 15_000,
    refetchInterval: APP_CATALOG_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  });
}

export function appQuery(appInstanceId: string) {
  return queryOptions({
    queryKey: ["apps", appInstanceId],
    queryFn: () => getApp(appInstanceId),
    staleTime: 15_000,
    refetchInterval: DEPLOYMENT_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  });
}

export function workspaceQuery(organizationId: string) {
  return queryOptions({
    queryKey: ["workspaces", organizationId, "admin"],
    queryFn: () => getWorkspace(organizationId),
    enabled: organizationId.length > 0,
    staleTime: 15_000,
  });
}

export function workspaceSubdomainQuery(organizationId: string) {
  return queryOptions({
    queryKey: ["workspaces", organizationId, "subdomain"],
    queryFn: () => getWorkspaceSubdomain(organizationId),
    enabled: organizationId.length > 0,
    staleTime: 15_000,
  });
}

export function useLoginMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: login,
    onSuccess: (me) => {
      queryClient.setQueryData(meQuery.queryKey, me);
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      queryClient.removeQueries();
      await queryClient.cancelQueries();
    },
  });
}
